import React, { useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '../stylesheets/AnalisisProfundo.css';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import 'jspdf/dist/polyfills.es.js';

import autoTable from 'jspdf-autotable';
import { FiCopy, FiInfo, FiDownload } from "react-icons/fi";
import Lottie from "lottie-react";
import hourglassAnimation from "../assets/C0DvzRjMMN.json";
import { HiExternalLink } from "react-icons/hi";

import rehypeRaw from 'rehype-raw';
import "@fontsource/roboto"; // 👈 importa Roboto (normal y ligera)

const AnalisisProfundo = () => {
  const [showInfo, setShowInfo] = useState(false);
  const [dot, setDot] = useState('');
  const [openMenu, setOpenMenu] = useState(null);

  const [licenciaFederal, setLicenciaFederal] = useState(''); // ← NUEVO
  const [camaraInput, setCamaraInput] = useState('');
  const [operationInput, setOperationInput] = useState([]);
  const [unitsTypeInput, setUnitsTypeInput] = useState([]);
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // ====== Utils ======

  const handleDownloadPDF = async () => {
    const element = document.querySelector('.analisis-profundobox');
    if (!element) return alert("No encontré el contenedor.");

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const margin = 15;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const contentWidth = pageWidth - (margin * 2);
      
      let yPosition = margin;

      // Función para limpiar HTML y entidades corruptas
      const cleanCellContent = (text) => {
        if (!text) return "";
        return text
          .replace(/&[^;]+;/g, " ") // Eliminar entidades HTML corruptas
          .replace(/<[^>]*>/g, "") // Eliminar tags HTML
          .replace(/[^\w\sáéíóúñÁÉÍÓÚÑ.,\-():%/@#&]/g, " ") // Limpiar caracteres extraños
          .replace(/\s+/g, " ") // Normalizar espacios
          .trim();
      };

      // Función para verificar página
      const checkPageBreak = (requiredSpace = 20) => {
        if (yPosition > pageHeight - requiredSpace) {
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // ====== ENCABEZADO DEL DOCUMENTO ======
      // Título principal
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Análisis Profundo', margin, yPosition);
      yPosition += 12;

      // DOT Number
      if (dot) {
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0);
        pdf.text(`DOT: ${dot}`, margin, yPosition);
        yPosition += 8;
      }

      // Nombre del asegurado (extraer del apiResponse si está disponible)
      let insuredName = '';
      if (apiResponse?.dot_info) {
        let dotInfoParsed = apiResponse.dot_info;
        if (typeof dotInfoParsed === "string") {
          try {
            dotInfoParsed = JSON.parse(dotInfoParsed);
          } catch { }
        }
        if (dotInfoParsed && typeof dotInfoParsed === "object") {
          insuredName = dotInfoParsed.LegalName || dotInfoParsed.DbaName || '';
        }
      }

      if (insuredName) {
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(`NOMBRE DEL ASEGURADO: ${insuredName.toUpperCase()}`, margin, yPosition);
        yPosition += 10;
      }

      // Línea separadora
      pdf.setDrawColor(20, 61, 140);
      pdf.setLineWidth(0.5);
      pdf.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;

      // ====== PROCESAR PERFIL ANALIZADO - RENDERIZAR POR SECCIONES ======
      if (apiResponse?.perfil_analizado_filtrado) {
        const perfilText = massageChameleon(apiResponse.perfil_analizado_filtrado)
          .replace(/^\[RESPUESTA\]\s*/g, '')
          .replace(/^```(?:\w+)?/, '')
          .replace(/```$/, '')
          .replace(/\n{3,}/g, '\n\n')
          .trim();

        console.log('Perfil Text:', perfilText); // Debug

        // Dividir contenido en secciones por encabezados principales
        const sections = [];
        let currentSection = { title: '', content: '' };
        const lines = perfilText.split('\n');

        for (const line of lines) {
          const trimmed = line.trim();
          
          // Detectar encabezado principal (###, # **, o **texto:**)
          const isMainHeader = trimmed.startsWith('###') || 
                               trimmed.startsWith('# **') || 
                               (trimmed.startsWith('**') && trimmed.endsWith(':**'));

          if (isMainHeader) {
            // Guardar sección anterior si tiene contenido
            if (currentSection.title && currentSection.content.trim()) {
              sections.push(currentSection);
            }

            // Extraer título limpio
            const title = trimmed
              .replace(/^###\s*/, '')
              .replace(/^#\s*\*\*/, '')
              .replace(/^\*\*/, '')
              .replace(/\*\*:?\s*$/, '')
              .replace(/:\*\*$/, '')
              .trim();

            currentSection = { title, content: '' };
          } else if (trimmed) {
            // Agregar línea al contenido de la sección actual
            currentSection.content += line + '\n';
          }
        }

        // Guardar última sección
        if (currentSection.title && currentSection.content.trim()) {
          sections.push(currentSection);
        }

        // Renderizar cada sección
        const titleTranslations = {
          // Secciones Principales (Markdown Headers)
          "Extracted Information": "Información Extraída",
          "Specific Questions": "Preguntas Específicas",
          "Suggested Carriers": "Carriers Sugeridos",
          "Final Notes": "Notas Finales",
          "Professional Profile": "Perfil Profesional",
          "Company Overview": "Resumen de la Compañía",
          "Driver Information": "Información del Conductor",
          "Safety and Claims": "Seguridad y Reclamaciones",
          "Safety and Operational Details": "Detalles de Seguridad y Operación",
          "Claims History": "Historial de Reclamaciones",
          "Document Validity": "Validez de Documentos",
          "Insurance and Underwriting Considerations": "Consideraciones de Seguro y Suscripción",
          "Safety and Accidents": "Seguridad y Accidentes",
          "Drivers": "Conductores",
          "Documentation Status": "Estado de Documentación",
          "Extracted Information for Underwriting and Carrier Selection": "Información Extraída para Suscripción y Selección de Carrier",
          "Answers to Specific Questions": "Respuestas a Preguntas Específicas",
          "DOT and Carrier Information": "Información de DOT y Carrier",
          "Operations and Cargo": "Operaciones y Carga",
          "Equipment Details": "Detalles del Equipo",
          "Loss History": "Historial de Siniestros",

          // Sub-secciones detectadas como labels (**Text:**)
          "Years in Business": "Años en el Negocio",
          "Current Insurance Carrier": "Carrier de Seguro Actual",
          "Number of Power Units": "Número de Unidades de Poder",
          "DOT Safety Rating": "Calificación de Seguridad DOT",
          "Type of Cargo": "Tipo de Carga",
          "Type of Operation": "Tipo de Operación",
          "Type of Units": "Tipo de Unidades",
          "Number of Accidents": "Número de Accidentes",
          "Injuries or Fatalities": "Lesiones o Fatalidades",
          "Number of Drivers": "Número de Conductores",
          "Driver Details": "Detalles del Conductor",
          "Safety/Accidents": "Seguridad/Accidentes",
          "Fleet and Operations": "Flota y Operaciones",
          "Safety Performance": "Desempeño de Seguridad",
          "Inspection and Crash History": "Historial de Inspecciones y Choques",
          "Insurance Considerations": "Consideraciones de Seguro",
          "Risk Analysis": "Análisis de Riesgo",
          "Analysis": "Análisis",
          "Conclusion": "Conclusión",
          "Recommendations": "Recomendaciones",
          "Type of Cargo and Operation": "Tipo de Carga y Operación",
          "Number of Drivers and Driver Details": "Número de Conductores y Detalles del Conductor"
        };

        for (const section of sections) {
          checkPageBreak(30);
          
          // Traducir título si existe en el mapeo (manejando numeración, mayúsculas y caracteres especiales)
          let spanishTitle = section.title;
          
          // Función de normalización para busqueda robusta
          const normalize = (s) => s.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
          
          // Intentar match con el título limpio (quitando numeración inicial)
          const cleanTitleMatch = section.title.match(/^(?:\d+\.\s*)?(.+)$/);
          if (cleanTitleMatch) {
            const cleanTitle = cleanTitleMatch[1].trim();
            const cleanTitleNorm = normalize(cleanTitle);
            
            // Buscar en el diccionario
            const entryKey = Object.keys(titleTranslations).find(
              key => normalize(key) === cleanTitleNorm
            );

            if (entryKey) {
              // Reemplazar el texto original por la traducción, manteniendo la numeración si existe
              spanishTitle = section.title.replace(cleanTitle, titleTranslations[entryKey]);
            }
          }

          // Título de la sección con fondo azul
          pdf.setFillColor(20, 61, 140);
          pdf.rect(margin, yPosition - 4, contentWidth, 8, 'F');
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(255, 255, 255);
          pdf.text(spanishTitle, margin + 2, yPosition);
          yPosition += 10;
          pdf.setTextColor(0, 0, 0);

          // Preparar contenido para la tabla
          const contentLines = section.content.trim().split('\n');
          const tableData = [];
          
          for (const contentLine of contentLines) {
            const trimmed = contentLine.trim();
            if (trimmed) {
              // Limpiar el texto
              let displayText = cleanCellContent(trimmed);
              tableData.push([displayText]);
            }
          }

          // Renderizar contenido como tabla
          if (tableData.length > 0) {
            autoTable(pdf, {
              body: tableData,
              startY: yPosition,
              margin: { left: margin, right: margin },
              styles: {
                fontSize: 9,
                cellPadding: 3,
                overflow: 'linebreak',
                lineColor: [220, 220, 220],
                lineWidth: 0.1,
                textColor: [0, 0, 0]
              },
              columnStyles: {
                0: {
                  cellWidth: 'auto'
                }
              },
              theme: 'grid',
              pageBreak: 'auto'
            });

            yPosition = pdf.lastAutoTable.finalY + 8;
          }
        }

        yPosition += 5;
      }

      // ====== PROCESAR DOT INFO ======
      if (apiResponse?.dot_info) {
        checkPageBreak(50);
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(20, 61, 140);
        pdf.text('Información del DOT', margin, yPosition);
        yPosition += 8;
        pdf.setTextColor(0, 0, 0);

        let dotInfoParsed = apiResponse.dot_info;

        // Parsear si viene como string
        if (typeof dotInfoParsed === "string") {
          try {
            dotInfoParsed = JSON.parse(dotInfoParsed);
          } catch {
            // Si no es JSON, intentar parsear como TSV
            const lines = dotInfoParsed.split("\n").filter(Boolean);
            if (lines.length >= 2) {
              const headers = lines[0].trim().split(/\s{2,}|\t/);
              const values = lines[1].trim().split(/\s{2,}|\t/);
              dotInfoParsed = {};
              headers.forEach((h, i) => { 
                dotInfoParsed[h.trim()] = values[i] || ""; 
              });
            }
          }
        }

        if (dotInfoParsed && typeof dotInfoParsed === "object") {
          const dotInfoEntries = Object.entries(dotInfoParsed);
          
          // Crear tabla con 4 columnas (2 pares de Campo|Valor)
          const rows = [];
          for (let i = 0; i < dotInfoEntries.length; i += 2) {
            const [key1, value1] = dotInfoEntries[i];
            const val1 = value1 === null || value1 === undefined ? "" : 
                         Array.isArray(value1) ? value1.join(", ") : String(value1);
            
            if (i + 1 < dotInfoEntries.length) {
              const [key2, value2] = dotInfoEntries[i + 1];
              const val2 = value2 === null || value2 === undefined ? "" : 
                           Array.isArray(value2) ? value2.join(", ") : String(value2);
              rows.push([key1, val1, key2, val2]);
            } else {
              rows.push([key1, val1, '', '']);
            }
          }

          autoTable(pdf, {
            body: rows,
            startY: yPosition,
            margin: { left: margin, right: margin },
            styles: { 
              fontSize: 7,
              cellPadding: 2,
              overflow: 'linebreak',
              lineColor: [220, 220, 220],
              lineWidth: 0.1
            },
            columnStyles: {
              0: { 
                fontStyle: 'bold', 
                fillColor: [20, 61, 140],
                cellWidth: 45,
                textColor: [255, 255, 255]
              },
              1: { 
                cellWidth: 'auto',
                textColor: [0, 0, 0],
                fillColor: [255, 255, 255]
              },
              2: { 
                fontStyle: 'bold', 
                fillColor: [20, 61, 140],
                cellWidth: 45,
                textColor: [255, 255, 255]
              },
              3: { 
                cellWidth: 'auto',
                textColor: [0, 0, 0],
                fillColor: [255, 255, 255]
              }
            },
            theme: 'grid',
            pageBreak: 'auto'
          });

          yPosition = pdf.lastAutoTable.finalY + 10;
        }
      }

      // ====== CARRIERS SUGERIDOS ======
      const guidelineCarriers = ensureArrayCarriers(apiResponse?.carrier_probabilities_guideline);
      const antesCarriers = ensureArrayCarriers(apiResponse?.carrier_probabilities_antes);
      const carriersToRender = guidelineCarriers.length > 0 ? guidelineCarriers : antesCarriers;

      if (carriersToRender.length > 0) {
        checkPageBreak(50);
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(20, 61, 140);
        pdf.text('Carriers Sugeridos', margin, yPosition);
        yPosition += 8;
        pdf.setTextColor(0, 0, 0);

        const carrierData = carriersToRender.map(carrier => [
          carrier.InsCarrier || carrier.Carrier || '',
          carrier.Frecuencia || '0',
          (carrier.Porcentaje ? carrier.Porcentaje + (carrier.Porcentaje.toString().includes('%') ? '' : '%') : '0%')
        ]);

        autoTable(pdf, {
          head: [['Carrier sugerido', 'Frecuencia', 'Porcentaje']],
          body: carrierData,
          startY: yPosition,
          margin: { left: margin, right: margin },
          styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak' },
          headStyles: { fillColor: [20, 61, 140], textColor: 255, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [240, 240, 240] },
          columnStyles: {
            0: { cellWidth: 'auto' },
            1: { cellWidth: 30, halign: 'center' },
            2: { cellWidth: 30, halign: 'center' }
          },
          pageBreak: 'auto'
        });

        yPosition = pdf.lastAutoTable.finalY + 10;
      }

      const pdfName = `AnalisisProfundo_${dot || 'resultado'}.pdf`;
      pdf.save(pdfName);

    } catch (error) {
      console.error('Error generando PDF:', error);
      alert('Error al generar el PDF. Por favor, intenta nuevamente.');
    }
  };




  const DotInfoTable = ({ dotInfo }) => {
    let parsed = dotInfo;

    if (typeof parsed === "string") {
      try {
        parsed = JSON.parse(parsed); // si viene como JSON string
      } catch {
        console.warn("dotInfo no era JSON, intentando parsear TSV");
        parsed = parseTSV(dotInfo); // 👈 conviertes TSV en objeto
      }
    }

    if (!parsed || typeof parsed !== "object") return <div>DOT Info no válido</div>;

    return (
      <div className="dot-info">
        <h4><strong>DOT Info</strong></h4>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                {Object.keys(parsed).map((key) => (
                  <th key={key}>{key}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {Object.values(parsed).map((value, idx) => (
                  <td key={idx}>
                    {value === null || value === undefined
                      ? ""
                      : Array.isArray(value)
                      ? value.join(", ")
                      : String(value)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Función para parsear TSV/espacios a diccionario
  function parseTSV(tsvString) {
    const lines = tsvString.split("\n").filter(Boolean);
    if (lines.length < 2) return {};

    const headers = lines[0].trim().split(/\s{2,}|\t/); // separa por tabs o múltiples espacios
    const values = lines[1].trim().split(/\s{2,}|\t/);

    const dict = {};
    headers.forEach((h, i) => { dict[h.trim()] = values[i] || ""; });

    return dict;
  }





  // Función para asegurar que los carriers sean un array (maneja string TSV)
  const ensureArrayCarriers = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'string') {
      const lines = data.split('\n').filter(line => line.trim());
      if (lines.length < 2) return [];
      
      const headers = lines[0].trim().split(/\s{2,}|\t/).map(h => h.trim());
      return lines.slice(1).map(line => {
        const values = line.trim().split(/\s{2,}|\t/);
        const obj = {};
        headers.forEach((h, i) => {
          // Normalizar nombres de llaves comunes
          let key = h;
          if (/^inscarrier$/i.test(key)) key = "InsCarrier";
          if (/^carrier$/i.test(key)) key = "InsCarrier";
          obj[key] = (values[i] || '').trim();
        });
        return obj;
      });
    }
    return [];
  };

  const massageChameleon = (s = '') => {
    let out = String(s);

    // 🧹 Limpieza general
    out = out
      .replace(/^\s*\*?\*?\s*(?:chameleon|camaleon)\s*tipo\s*[12]\s*:\s*(?:si|no)(?:\s*TIENE)?\s*\*?\*?\s*$/gim, '')
      .replace(/^\s*(?:\*\*)?\s*(?:chameleon|camaleon)\s*tipo\s*(1|2)\s*:?(?:\*\*)?\s*$/gim, '\n\n### Chameleon Tipo $1:\n')
      .replace(/(?:\n+### Chameleon Tipo (1|2):\s*){2,}/g, '\n\n### Chameleon Tipo $1:\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    // 🚨 Validaciones numéricas
    [
      { key: "# Crash 2 Yr", emoji: "🚨", msg: "Explica qué pasó y si está validado en Loss Run", min: 1 },
      { key: "# Injuries 2 Yr", emoji: "🚓", msg: "Debes tener un reporte policial y dar una explicación clara", min: 1 },
      { key: "# Fatalities 2 Yr", emoji: "💀", msg: "Debes tener un reporte policial y dar una explicación clara", min: 1 },
      { key: "# Basic Alerts", emoji: "⚡", msg: "Debe presentarse un Safety Plan firmado por el cliente", min: 2 },
    ].forEach(({ key, emoji, msg, min }) => {
      const pattern = new RegExp(`\\|\\s*${key}\\s*\\|([^|]*)\\|`, "i");
      out = out.replace(pattern, (match, val) => {
        const num = parseInt(val);
        if (!isNaN(num) && num >= min) {
          return `| ${key} | ${num} ${emoji} <span class="alerta-crash">${msg}</span> |`;
        }
        return match;
      });
    });

    // 🚛 Validar antigüedad unidad
    const currentYear = new Date().getFullYear();
    const modelPattern = /\|\s*Power unit Oldest model\s*\|([^|]*)\|/i;
    out = out.replace(modelPattern, (match, val) => {
      const year = parseInt(val.trim());
      if (!isNaN(year)) {
        const antiguedad = currentYear - year;
        if (antiguedad >= 20) {
          return `| Power unit Oldest model | ${year} 🚨 <span class="alerta-crash">Unidad con ${antiguedad} años — adjuntar Mechanical Statement 🧾</span> |`;
        }
      }
      return match;
    });

    // 👤 Estado del owner — alerta si NO contiene 'activo'
    const ownerPattern = /\|\s*Estado\s*del\s*owner\s*\|([^|]*)\|/i;
    out = out.replace(ownerPattern, (match, val) => {
      const texto = val
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\n/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();

      if (!texto.includes("activo")) {
        return `| Estado del owner | ${val.trim()} 🚨 <span class="alerta-owner">El owner no está activo — debe explicar la situación y adjuntar CDL o MVR en el sometimiento </span> |`;
      }
      return match;
    });

    // 🧾 Validación de inspecciones "SI TIENE ..."
    const inspeccionPattern = /\|\s*(?:¿?Tiene\s+Inspecciones\??(?:\s*\(Carrier\s*Software\))?)\s*\|([^|]*)\|/i;
    out = out.replace(inspeccionPattern, (match, val) => {
      const textoOriginal = val.trim();
      const texto = textoOriginal
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\n/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();

      if (/\b(si\s*tiene|yes\s*has)\b/.test(texto)) {
        const alerta = `<span class="alerta-inspeccion"> Justificar por qué esas unidades no van a ir en ese nuevo término. <a href="https://cmvid.nisrinc.com/CMV_ID/CMV_ID.asp" target="_blank" rel="noopener noreferrer" class="alerta-link">Valida los VINs aquí 🔍</a></span> `;

        // 🔹 Inserta la alerta justo antes de "SI TIENE" (manteniendo el resto del texto intacto)
        const textoConAlerta = textoOriginal.replace(/(si\s*tiene)/i, `${alerta}$1`);

        return `| ¿Tiene Inspecciones? (Carrier Software) | ${textoConAlerta} |`;
      }

      return match;
    });

    return out;
  };



  const TableOrMarkdown = ({
    text = '',
    title,
    className,
    scroll = false,
    headerDelimiter = 'auto',
    rowDelimiter = 'auto',
    customTitleClass,
    scrollForTitles = [],
  }) => {

    const stateLinks = {
      FL: "http://search.sunbiz.org/Inquiry/CorporationSearch/ByName",
      TX: "https://mycpa.cpa.state.tx.us/coa/",
      CA: "https://bizfileonline.sos.ca.gov/search/business",
      PA: "https://file.dos.pa.gov/search/business",
      AZ: "https://ecorp.azcc.gov/AzAccount?sessionExpired=False",
      IL: "https://apps.ilsos.gov/businessentitysearch/",
      GA: "https://ecorp.sos.ga.gov/BusinessSearch",
    };
    let clean = String(text).trim();
    clean = clean.replace(/(\*\*[^:\n]+:\*\*)(?=\s*\|)/g, '$1\n\n');
    clean = clean
      .normalize("NFKD")
      .replace(/[\u0400-\u04FF]/g, '') 

    if (!clean) return null;

    const flattenText = (nodes) => {
      const arr = React.Children.toArray(nodes);
      return arr.map((n) => {
        if (typeof n === 'string' || typeof n === 'number') return String(n);
        if (React.isValidElement(n)) return flattenText(n.props?.children);
        return '';
      }).join('');
    };

    const normalize = (s) =>
      String(s || '')
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\p{L}\p{N}\s:()\-]/gu, '')
        .replace(/\s+/g, ' ')
        .trim();

    const shouldWrap = (sectionText) => {
      if (!sectionText) return false;
      const norm = normalize(sectionText);
      return scrollForTitles.some(t => norm.includes(normalize(t)));
    };

    const decoratedHeadings = [
      /^(historial de carriers)$/i,
      /^(historial de drivers)$/i,
      /^(historial de loss runs)$/i,
      /^(chameleon tipo 1)$/i,
      /^(chameleon tipo 2)$/i,
    ];

    const looksLikeMdTable = /\|.+\|/.test(clean) && /(\n|\r)\s*\|?-{3,}/.test(clean);

    // ======= RENDER MARKDOWN SIEMPRE QUE SE VEA COMO TABLA =======
    if (
      looksLikeMdTable &&
      headerDelimiter === 'auto' &&
      rowDelimiter === 'auto'
    ) {
      let currentSection = '';

      const md = (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
 
        components={{
          h3({ children, ...props }) {
            const plain = flattenText(children).trim();
            currentSection = plain;
            const matchDecorated = decoratedHeadings.some(r => r.test(plain));
            const base = 'md-section-title';
            return (
              <h3
                {...props}
                className={`${base} ${matchDecorated ? 'titulo-analisis' : ''} ${props.className || ''}`.trim()}
              >
                {children}
              </h3>
            );
          },
          td({ children, node, ...props }) {
            const val = flattenText(children).trim();
            const valUpper = val.toUpperCase();

            // 1️⃣ Estado con link
            if (stateLinks[valUpper]) {
              return (
                <td {...props}>
                  <a
                    href={stateLinks[valUpper]}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Comprobar con la Secretaría de Estado"
                    style={{
                      color: '#143D8C',
                      fontWeight: 600,
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <span style={{ color: '#000' }}>{valUpper}</span>
                    <span
                      style={{
                        fontSize: '0.85rem',
                        color: '#143D8C',
                        textDecoration: 'underline',
                      }}
                    >
                      Verificar en la Secretaría
                    </span>
                    <HiExternalLink size={15} />
                  </a>
                </td>
              );
            }

            // 2️⃣ Detección YIB row
            const maybeRow = node?.position?.start?.line;
            const fullText = clean.split('\n');
            const currentLine = fullText[maybeRow - 1] || '';
            const isYIBRow =
              currentLine.toLowerCase().includes('yib') &&
              currentLine.toLowerCase().includes('appquote');

            // ✅ Verificar si esta celda ES el encabezado YIB
            const isYIBHeader =
              val.toLowerCase().includes('yib') &&
              val.toLowerCase().includes('appquote');

            // ✅ Solo añadir (vs CS: ...) si:
            // - Estamos en la fila YIB
            // - NO es la celda del encabezado
            // - Existe el valor en apiResponse
            if (
              isYIBRow &&
              !isYIBHeader &&
              apiResponse?.dot_info?.YearsInBusiness !== undefined
            ) {
              const yibCS = apiResponse.dot_info.YearsInBusiness;
              const valClean = val.replace(/\(vs\s*CS[:：][^)]+\)/gi, '').trim();

              return (
                <td {...props}>
                  {valClean}{' '}
                  <span
                    style={{
                      color: '#6b7280',
                      fontSize: '0.9em',
                      marginLeft: '4px',
                    }}
                    title={`Comparación con CarrierSoftware: ${yibCS} años`}
                  >
                    (vs CS: {yibCS} años)
                  </span>
                </td>
              );
            }






            // 3️⃣ Normal
            return <td {...props}>{children}</td>;
          },





          table({ children, ...props }) {
            const tbl = <table className="data-table" {...props}>{children}</table>;

            const normalized = (currentSection || '')
              .toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, '');

            const isPerfil = normalized.includes('perfil de la compania');

            // ✅ sólo agrega scroll si NO es "Perfil de la compañía"
            return isPerfil
              ? tbl
              : <div className="table-scroll">{tbl}</div>;
          },
        }}
      >
        {clean}
      </ReactMarkdown>
      );

      return (
        <div className={className}>
          {title && <h4 className={customTitleClass}>{title}</h4>}
          {md}
        </div>
      );
    }

    // ======= Fallback a TSV / Espacios =======
    const pickSplitter = (mode, sampleRows) => {
      if (mode === 'whitespace') return /\s+/;
      if (mode === 'spaces2') return /\s{2,}/;
      if (mode === 'tsv') return /\t+/;
      return sampleRows.some(r => r.includes('\t')) ? /\t+/ : /\s{2,}/;
    };

    const rowsRaw = clean.split(/\r?\n/).map(r => r.trimEnd()).filter(Boolean);
    if (rowsRaw.length === 0) return null;

    const headSplit = pickSplitter(headerDelimiter, rowsRaw);
    let header = rowsRaw[0].split(headSplit).filter(Boolean).map(s => s.trim());
    header = header.map(h => /^inscarrier$/i.test(h) ? 'Carrier sugerido' : h);
    const expectedCols = header.length;

    const bodyLines = rowsRaw.slice(1);
    const combinedRows = [];
    let currentRow = '';
    console.log("🧩 HEADERS:", header);
    console.log("📄 combinedRows:", combinedRows);
    console.log("📊 expectedCols:", expectedCols);
    for (let line of bodyLines) {
      if (!currentRow) {
        currentRow = line;
      } else {
        const cells = currentRow.split(pickSplitter(rowDelimiter, [currentRow]));
        if (cells.length < expectedCols) {
          currentRow += ' ' + line;
        } else {
          combinedRows.push(currentRow);
          currentRow = line;
        }
      }
    }
    if (currentRow) combinedRows.push(currentRow);

    const body = combinedRows
      .map(row => {
        const cells = row.split(pickSplitter(rowDelimiter, [row])).map(s => s.trim());
        if (cells.length < expectedCols) return cells.concat(Array(expectedCols - cells.length).fill(''));
        if (cells.length > expectedCols) {
          return [
            cells.slice(0, cells.length - (expectedCols - 1)).join(' '),
            ...cells.slice(cells.length - (expectedCols - 1))
          ];
        }
        return cells;
      })
      .map(cells => {
        const idx = header.findIndex(h => h.replace(/\s/g, '').toLowerCase() === 'porcentaje');
        if (idx !== -1 && !isNaN(parseFloat(cells[idx]))) {
          const val = parseFloat(cells[idx]);
          cells[idx] = `${Math.round(val)}%`;
        }
        return cells;
      });

    const tableEl = (
      <table
      className={`data-table ${
        scroll ? 'data-table--wide' : ''
      } ${className?.includes('carrier-probabilities-3d') ? 'data-table-3d' : ''}`}
      >
        <thead>
          <tr>{header.map((h, i) => <th key={i}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {body.map((cells, i) => (
            <tr key={i}>
              {cells.map((c, j) => (
                <td key={j}>
                  {(() => {
                    const val = String(c || '').trim();
                    const colName = header[j]?.toLowerCase?.() || '';



                    // Mensajes adicionales por estado (opcional)
                    const stateTips = {
                      CA: "Debe tener MCP e inscripción en el CA DMV",
                      IL: "Verificar Filing Estatal Illinois (IL ICC Number)",
                    };

                    // Si la columna es State y el valor está en el diccionario
                    const valClean = val.toUpperCase().trim();

                    // Mostrar link solo si la columna incluye "state"
                    if (colName.includes('state') && stateLinks[valClean]) {
                      const tip = stateTips[valClean] || `Ir a Secretaría de Estado de ${valClean}`;
                      return (
                        <a
                          href={stateLinks[valClean]}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={tip}
                          style={{
                            color: '#143D8C',
                            fontWeight: 600,
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          <span style={{ color: '#000' }}>{valClean}</span>
                          <span style={{
                            fontSize: '0.85rem',
                            color: '#143D8C',
                            textDecoration: 'underline',
                          }}>
                            Rectifica información
                          </span>
                          <HiExternalLink size={15} />
                        </a>
                      );
                    }



                    // Valor normal (sin link)
                    return val;
                  })()}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );

    return (
      <div className={className}>
        {title && <h4 className={customTitleClass}>{title}</h4>}
        {scroll ? <div className="table-scroll">{tableEl}</div> : tableEl}
      </div>
    );
  };




  // ====== Copy (excluye DOT Info) ======
  const handleCopyAllExceptDotInfo = useCallback(() => {
    const root = document.querySelector('.analisis-profundobox');
    if (!root) return alert('No encontré el contenedor.');

    const clone = root.cloneNode(true);
    clone.querySelectorAll('.dot-info, form, .copy-btn, .file-drop-zone, .selected-files').forEach(n => n.remove());

    const style = `
      <style>
        body { font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; }
        h1,h2,h3,h4 { margin: 0.4rem 0 0.6rem; }
        p, li { line-height: 1.4; }
        hr { border: 0; border-top: 1px solid #e5e7eb; margin: 12px 0; }
        table { border-collapse: collapse; width: 100%; margin: 8px 0; }
        th, td { border: 1px solid #ccc; padding: 8px; text-align: left; vertical-align: top; }
        tr:nth-child(even) { background: #f9f9f9; }
        th { background: #f0f4ff; color: #1d4ed8; }
        .table-title-bar h4 { background:#143D8C; color:#fff; padding:10px 14px; border-radius:10px; display:inline-block; }
      </style>
    `;

    const html = style + clone.innerHTML;
    const text = clone.innerText;

    if (!window.ClipboardItem) {
      navigator.clipboard.writeText(text)
        .then(() => alert('Copiado como texto (sin formato)'))
        .catch(err => alert('Error copiando: ' + err));
      return;
    }

    navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      })
    ])
    .then(() => alert('✅ Copiado'))
    .catch(err => alert('❌ Error al copiar: ' + err));
  }, []);

  // ====== Handlers drag&drop ======
  const processFiles = useCallback((picked) => {
    const pdfs = picked.filter(f => f.type === 'application/pdf');
    setFiles(pdfs);
  }, []);

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    processFiles(selected);
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => { e.preventDefault(); setIsDragging(false); processFiles(Array.from(e.dataTransfer.files)); };

  // ====== Submit (ENDPOINT PROFUNDO) ======
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!dot.trim()) return alert('El campo DOT es obligatorio');
    if (!licenciaFederal.trim()) return alert('El campo Licencia Federal es obligatorio');

    setApiResponse(null);
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('dot_number', dot);
      formData.append('licenciaFederal', licenciaFederal); 
      formData.append('camara_input', camaraInput);
      const flattenOps = Array.isArray(operationInput)
        ? operationInput.flatMap(op => String(op).split(',').map(s => s.trim()))
        : String(operationInput).split(',').map(s => s.trim());

      formData.append('operation_input', flattenOps.join(','));

      const flattenUnits = Array.isArray(unitsTypeInput)
        ? unitsTypeInput.flatMap(u => String(u).split(',').map(s => s.trim()))
        : String(unitsTypeInput).split(',').map(s => s.trim());

      formData.append('units_type_input', flattenUnits.join(','));



      if (files.length > 0) files.forEach((f) => formData.append('rutas_pdfs', f));

      const response = await fetch('https://carrierprobapi.intruckscorp.com/analisis_por_guideline', {
        method: 'POST',
        headers: { accept: 'application/json' },
        body: formData,
      });

      if (!response.ok) throw new Error(`Error en la solicitud: ${response.statusText}`);

      // 🔍 Mostrar texto crudo de la API
      const rawText = await response.text();
      console.log("🧾 RAW RESPONSE (texto puro de la API):\n", rawText);

      // Intentar parsear como JSON (si aplica)
      try {
        const data = JSON.parse(rawText);
        console.log("✅ Parsed JSON:", data);
        setApiResponse(data);
      } catch (err) {
        console.warn("⚠️ No es JSON válido, mostrando texto crudo en pantalla");
        setApiResponse({ raw_output: rawText });
      }
    } catch (err) {
      console.error('Error enviando el formulario:', err);
      alert('Error enviando el formulario: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ====== Render ======
  return (
    <div className="right-section">
      <div className="analisis-profundobox">
        {isLoading && (
          <div className="overlay-loader">
            <Lottie
              animationData={hourglassAnimation}
              loop={true}
              style={{ width: 320, height: 320 }}
            />
            <h2 className="loading-title">
              Espéranos 1 minutico...
              <br />
              <span className="highlight">Estamos perfilando tu cliente 🚛</span>
            </h2>
   
          </div>
        )}

        <div className="title-row">
          <h1>Análisis Profundo</h1>

          <button
            type="button"
            className={`info-chip ${showInfo ? 'open' : ''}`}
            onClick={() => setShowInfo(v => !v)}
            aria-label="¿Qué es Análisis Profundo?"
            aria-expanded={showInfo}
          >
            <FiInfo size={18} />
          </button>

          <div
            className={`info-tooltip ${showInfo ? 'show' : ''}`}
            role="tooltip"
          >
            Este análisis realiza un <strong>profiling detallado</strong> del usuario, 
            combinando la información del App Quote con documentos adicionales como {" "} 
            <em>Loss Runs</em> y <em>MVRs</em>.  
            Identifica riesgos, consolida el historial y sugiere <strong>Carriers filtrados según todas las reglas de negocio (Guidelines) que posee la empresa</strong>.  
            Úsalo como diagnóstico completo antes de tomar decisiones.
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label><strong>DOT*</strong></label>
            <input
              type="text"
              value={dot}
              onChange={(e) => setDot(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="licencia-federal" className="label-with-tooltip">
            <strong>Licencia Federal*</strong>
            <span className="mini-tooltip" role="tooltip">
              Documento oficial emitido por la <strong>Secretaría de Transportes de México</strong>. 
              Esta licencia le permite al conductor manejar vehículos que realizan transporte de carga o pasajeros a nivel federal, 
              incluyendo viajes hacia <strong>Estados Unidos</strong>.
            </span>
            </label>

            <div className="select-wrap">
              <select
                id="licencia-federal"
                className="select-licencia"
                value={licenciaFederal}
                onChange={(e) => setLicenciaFederal(e.target.value)}
                required
              >
                <option value="">Seleccione…</option>
                <option value="SI">SI</option>
                <option value="NO">NO</option>
              </select>
            </div>
          
          
          </div>
          <div className="form-group">
            <label><strong>¿Tiene cámara?*</strong></label>
            <div className="select-wrap">
              <select
                id="camara-input"
                className="select-camara"
                value={camaraInput}
                onChange={(e) => setCamaraInput(e.target.value)}
                required
              >
                <option value="">Seleccione…</option>
                <option value="SI">SI</option>
                <option value="NO">NO</option>
              </select>
            </div>
          </div>
          {/* ===== OPERACIONES ===== */}
          <div className="form-group">
            <label><strong>Operaciones (Selección múltiple)*</strong></label>
            <div
              className={`custom-multiselect ${openMenu === 'operaciones' ? 'open' : ''}`}
              onClick={() => setOpenMenu(openMenu === 'operaciones' ? null : 'operaciones')}
            >
              <div className="selected-values">
                {operationInput.length > 0 ? operationInput.join(', ') : 'Selecciona Operaciones'}
              </div>
              <div className="arrow"></div>

              <div className="options">
                {[
                  "DRYVAN","FLATBED","REEFER","UIIA","CAR HAULER","AGRICULTURE","OS/OW",
                  "TOWING","INTERMODAL","DIRT SAND AND GRAVEL","DIRT SAND & GRAVEL",
                  "BUSES","HAZMAT","HOT SHOT (NO TX)","LIVESTOCK",
                  "HOTSHOT (NO CAR HAULER)","TANKER TRAILERS","SAND AND GRAVEL",
                  "NOT HAZMAT TANKER","HOTSHOT","OVERSIZE/OVERWEIGHT"
                ].map(op => (
                  <label key={op} className="option-item">
                    <input
                      type="checkbox"
                      checked={operationInput.includes(op)}
                      onChange={() => {
                        if (operationInput.includes(op)) {
                          setOperationInput(operationInput.filter(o => o !== op));
                        } else {
                          setOperationInput([...operationInput, op]);
                        }
                      }}
                    />
                    {op}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* ===== TIPO DE UNIDAD ===== */}
          <div className="form-group">
            <label><strong>Tipo de unidad (Selección múltiple)*</strong></label>
            <div
              className={`custom-multiselect ${openMenu === 'unidades' ? 'open' : ''}`}
              onClick={() => setOpenMenu(openMenu === 'unidades' ? null : 'unidades')}
            >
              <div className="selected-values">
                {unitsTypeInput.length > 0 ? unitsTypeInput.join(', ') : 'Selecciona tipos de unidad'}
              </div>
              <div className="arrow"></div>

              <div className="options">
                {[
                  "TRACTOR TRUCK","DUMP TRUCK","BOX TRUCK","PICK UP","SRAIGHT TRUCK",
                  "CARGO VAN","PICKUP","BOX TRUCKS","TRACTOR TRUCKS","DUMP TRUCKS",
                  "DUMP TRACTOR","SPRINTER VANS","TRACTOR"
                ].map(unit => (
                  <label key={unit} className="option-item">
                    <input
                      type="checkbox"
                      checked={unitsTypeInput.includes(unit)}
                      onChange={() => {
                        if (unitsTypeInput.includes(unit)) {
                          setUnitsTypeInput(unitsTypeInput.filter(u => u !== unit));
                        } else {
                          setUnitsTypeInput([...unitsTypeInput, unit]);
                        }
                      }}
                    />
                    {unit}
                  </label>
                ))}
              </div>
            </div>
          </div>


            <div className="form-group">
              <label className="label-with-tooltip">
                <strong>Subir Documentos (PDF)</strong>
                <span className="mini-tooltip" role="tooltip">
                  <strong>Debes subir: </strong>  
                  • App Quote  
                  • Loss Run  
                  • MVR  
                </span>
              </label>

              <div
                className={`file-drop-zone ${isDragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
              <div className="file-drop-content">
                <svg className="upload-icon" viewBox="0 0 24 24">
                  <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                </svg>
                <p>Choose a file or drag & drop it here</p>
                <p className="file-formats">PDF format, up to 50MB</p>
              </div>
              <input
                type="file"
                id="file-upload-profundo"
                accept="application/pdf"
                onChange={handleFileChange}
                multiple
                className="file-input"
              />
              <label htmlFor="file-upload-profundo" className="browse-btn">
                Browse File
              </label>
            </div>

            {files.length > 0 && (
              <div className="selected-files">
                <h4>Selected files:</h4>
                <ul>
                  {files.map((file, idx) => (
                    <li key={idx}>
                      <span>{file.name}</span>
                      <button
                        type="button"
                        onClick={() => setFiles(files.filter((_, i) => i !== idx))}
                        className="remove-btn"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <button type="submit" className="submit-btn" disabled={isLoading}>
            {isLoading ? 'Cargando…' : 'Calcular'}
          </button>

        </form>

        {/* Resultado del análisis profundo */}
        {apiResponse?.perfil_analizado_filtrado && (
          <TableOrMarkdown
            text={massageChameleon(apiResponse.perfil_analizado_filtrado)
              .replace(/^\[RESPUESTA\]\s*/g, '')
              .replace(/^```(?:\w+)?/, '')
              .replace(/```$/, '')
              .replace(/^###\s*Perfil de la Empresa.*$/im, '')
              .replace(/^\s*[-_]{3,}\s*$/gm, '')
              .replace(/^\s*#{1,6}\s*output\s*:?.*$/gim, '')
              .replace(/^\s*output\s*:?\s*$/gim, '')
              .replace(/\boutput\b:?\s*/gi, '')
              .replace(/\n{3,}/g, '\n\n')
              .trim()
            }
            title={
              <a
                href={`https://underwriting.carriersoftware.com/Analysis/SnapshotSimplified?dot=${dot}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#ffffffff',
                  textDecoration: 'none',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
                title={`Abrir perfil DOT ${dot} en Carrier Software`}
              >
                Resultado Análisis Profundo
                <HiExternalLink size={16} />
              </a>
            }
            className="perfil-analizado"
            customTitleClass="titulo-analisis"
            scrollForTitles={[
              'Historial de Drivers',
              'Historial de Loss Runs',
              'Chameleon Tipo 1',
              'Chameleon Tipo 2',
              'Historial de Carriers',
            ]}
          />
        )}


      {/* DOT Info (normalizado a TSV + scroll) */}
      {apiResponse?.dot_info && (
        <DotInfoTable dotInfo={apiResponse.dot_info} />
      )}

      {/* ⬆️ CARRIERS SUGERIDOS */}
      {(() => {
        const guidelineCarriers = ensureArrayCarriers(apiResponse?.carrier_probabilities_guideline);
        const antesCarriers = ensureArrayCarriers(apiResponse?.carrier_probabilities_antes);
        const carriers = guidelineCarriers.length > 0 ? guidelineCarriers : antesCarriers;

        // Si está vacío, no mostramos nada
        if (!carriers || carriers.length === 0) return null;

        return (
          <div className="carrier-probabilities-3d">
            <h4 className="titulo-analisis">Carriers Sugeridos</h4>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Carrier sugerido</th>
                    <th style={{ textAlign: 'center' }}>Frecuencia</th>
                    <th style={{ textAlign: 'center' }}>Porcentaje</th>
                  </tr>
                </thead>
                <tbody>
                  {carriers.map((carrier, index) => (
                    <tr key={index}>
                      <td>{carrier.InsCarrier || carrier.Carrier || ''}</td>
                      <td style={{ textAlign: 'center' }}>{carrier.Frecuencia || '0'}</td>
                      <td style={{ textAlign: 'center' }}>
                        {carrier.Porcentaje 
                          ? carrier.Porcentaje + (carrier.Porcentaje.toString().includes('%') ? '' : '%')
                          : '0%'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}



        {/* Botón flotante para copiar todo excepto DOT Info */}
        {apiResponse && (
          <div className="copy-container">
            <button
              type="button"
              onClick={handleCopyAllExceptDotInfo}
              className="copy-btn"
            >
              <FiCopy style={{ marginRight: 6 }} />
              Copiar resultado
            </button>
            <button
              type="button"
              onClick={handleDownloadPDF}
              className="copy-btn copy-btn--green"
              style={{ marginLeft: 8 }}
            >
              <FiDownload style={{ marginRight: 6 }} />
              Descargar PDF
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalisisProfundo;
